import { STATIONS, nearestStation } from "@frontier/content/stations";
import { firstLocalGate } from "../galaxy-navigation";
import { Container, Graphics, Sprite, Text } from "pixi.js";
import type { Application, Texture } from "pixi.js";
import type {
  PartyMarker,
  ShipSnapshot,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { AutopilotOverlay } from "./autopilot-overlay";
import type { NavigationContext, NavigationState } from "./navigation-types";
import { TRAINING_GATE_FACTIONS } from "@frontier/content/training";
import { Backdrop } from "./backdrop";
import { screenToWorld, worldToScreen } from "./geometry";
import type { Point } from "./geometry";
import type { GameInput } from "./input";
import { ShipView } from "./ship-view";
import type { ShipTextures } from "./ship-view";
import { LocalMovement } from "./local-movement";
import type {
  MissionObjective,
  MissionView,
} from "@frontier/content/contracts";
import { NavigationMarkers } from "./navigation-markers";
import { navigationCaption } from "./navigation-caption";
import type { NavigationTarget } from "./navigation-markers";
import { GateViews } from "./gate-views";
import { CombatEffects } from "./combat-effects";
import type { CombatTextures } from "./combat-effects";
import { attackableTargets, findAttackableTarget, physicalRegion } from "../targeting";
import { flightWorld } from "../flight-world";
import { galaxyRegionAt } from "../galaxy-navigation";
import { ResourceViews } from "./resource-views";
import { confirmedShipDeaths } from "./ship-lifecycle";
import { LootViews } from "./loot-views";
import { BeaconViews } from "./beacon-views";
import { PartyMarkerView } from "./party-marker";
import { RelayViews } from "./relay-views";

export interface SceneTextures extends ShipTextures {
  station: Texture;
  asteroids: readonly [Texture, Texture, Texture];
  container: Texture;
  combat: CombatTextures;
}

export class SectorScene {
  private readonly backdrop = new Backdrop();
  private readonly world = new Container();
  private readonly terrain = new Container();
  private readonly lootViews: LootViews;
  private readonly resources: ResourceViews;
  private readonly relayViews: RelayViews;
  private readonly beacons = new BeaconViews();
  private readonly autopilot = new AutopilotOverlay();
  private snapshotReceivedAt = 0;
  private readonly ground = new Graphics();
  private readonly actors = new Container();
  private readonly effects: CombatEffects;
  private readonly joystick = new Graphics();
  private readonly navigation = new NavigationMarkers();
  private readonly partyMarkerView = new PartyMarkerView();
  private readonly gates = new GateViews();
  private readonly ships = new Map<string, ShipView>();
  private readonly localMovement = new LocalMovement();
  private friends: readonly string[] = [];
  private readonly camera: Point = { x: 420, y: 1100 };
  private readonly backdropTilt: Point = { x: 0, y: 0 };
  private readonly smoothedBackdropTilt: Point = { x: 0, y: 0 };
  private readonly reducedMotion =
    typeof window === "undefined"
      ? undefined
      : window.matchMedia("(prefers-reduced-motion: reduce)");
  private snapshot: WorldSnapshot | undefined;
  private eventObjective: MissionObjective | null = null;
  private mission: MissionView | null = null;
  private flightRunId = "";
  private flightIntro = false;
  private tutorialGuidanceVisible = false;
  private partyMarker: PartyMarker | null = null;
  private localPlayerId = "";
  private staticKey = "";
  private selectedTarget = "";
  private zoom = 1;
  private firstPosition = true;

  constructor(
    private readonly app: Application,
    private readonly textures: SceneTextures,
  ) {
    this.effects = new CombatEffects(textures.combat);
    this.lootViews = new LootViews(textures.container);
    this.resources = new ResourceViews(textures.asteroids);
    this.relayViews = new RelayViews(textures.station);
    this.world.addChild(
      this.ground,
      this.terrain,
      this.resources.container,
      this.relayViews.container,
      this.lootViews.container,
      this.beacons.container,
      this.gates.container,
      this.autopilot.container,
      this.actors,
      this.effects.container,
      this.navigation.world,
      this.partyMarkerView.world,
    );
    app.stage.addChild(
      this.backdrop.container,
      this.world,
      this.navigation.screen,
      this.partyMarkerView.screen,
      this.joystick,
    );
  }

  setEventObjective(objective: MissionObjective | null): void {
    this.eventObjective = objective;
  }

  setBackdropTilt(point: Point): void {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    this.backdropTilt.x = Math.max(-10, Math.min(10, point.x));
    this.backdropTilt.y = Math.max(-10, Math.min(10, point.y));
  }

  setMission(mission: MissionView | null): void {
    this.mission = mission;
  }

  setFlightIdentity(runId: string, intro: boolean): boolean {
    if (this.flightRunId === runId && this.flightIntro === intro) return false;
    this.flightRunId = runId;
    this.flightIntro = intro;
    return true;
  }

  setPartyMarker(marker: PartyMarker | null): void {
    this.partyMarker = marker;
  }

  setTutorialGuidanceVisible(visible: boolean): void {
    this.tutorialGuidanceVisible = visible;
  }

  setLocalPlayerId(id: string): void {
    if (this.localPlayerId === id) return;
    this.localPlayerId = id;
    this.localMovement.reset();
    this.firstPosition = true;
    if (this.snapshot) this.lootViews.update(this.snapshot, this.localPlayerId);
    const ship = this.ships.get(id);
    if (ship) this.localMovement.receive(ship.state, this.snapshotReceivedAt);
  }

  setSnapshot(source: WorldSnapshot, receivedAt: number): void {
    const snapshot = flightWorld(source, this.localPlayerId);
    const now = performance.now();
    this.snapshot = snapshot;
    this.snapshotReceivedAt = receivedAt;
    this.effects.receive(snapshot, now);
    this.beacons.receive(snapshot.beacons ?? [], snapshot.time, now);
    this.syncTerrain(snapshot);
    this.resources.update(snapshot.obstacles, this.selectedTarget);
    const local = snapshot.players.find((ship) => ship.id === this.localPlayerId);
    this.gates.update(local ? snapshot.gates.filter((gate) => physicalRegion(gate) ===
      (snapshot.boundaryRules ? galaxyRegionAt(snapshot, local)?.id : physicalRegion(local))) : []);
    this.relayViews.update(snapshot.publicEvents);
    this.backdrop.setRegions(snapshot.regions);
    const deaths = confirmedShipDeaths(
      [...this.ships.values()].map((view) => view.state),
      [...snapshot.players, ...snapshot.npcs],
    );
    const ids = new Set<string>();
    for (const ship of [...snapshot.players, ...snapshot.npcs]) {
      ids.add(ship.id);
      let view = this.ships.get(ship.id);
      if (!view) {
        view = new ShipView(ship, this.textures, now);
        this.ships.set(ship.id, view);
        this.actors.addChild(view.container);
      }
      if (deaths.has(ship.id)) this.effects.explode(ship, 90, now);
      view.update(
        ship,
        now,
        ship.id === this.localPlayerId,
        ship.id === this.selectedTarget,
        ship.kind === "security" && ship.securityTargetId !== this.localPlayerId,
      );
      if (ship.id === this.localPlayerId)
        this.localMovement.receive(ship, receivedAt);
    }
    for (const [id, ship] of this.ships) {
      if (ids.has(id)) continue;
      ship.destroy();
      this.ships.delete(id);
    }
    this.lootViews.update(snapshot, this.localPlayerId);
  }

  setFriends(ids: readonly string[]): void {
    this.friends = ids;
  }
  isAttackable(id: string): boolean {
    const local = this.snapshot?.players.find(
      (ship) => ship.id === this.localPlayerId,
    );
    return !!(
      this.snapshot &&
      local &&
      findAttackableTarget(this.snapshot, local, id, this.friends)
    );
  }

  selectTarget(id: string): void {
    this.selectedTarget = id;
    this.resources.select(id);
    for (const [shipId, view] of this.ships) view.setSelected(id === shipId);
  }

  getLocalPosition(): Point {
    const ship = this.ships.get(this.localPlayerId);
    return ship
      ? this.firstPosition
        ? { x: ship.state.x, y: ship.state.y }
        : { x: ship.container.x, y: ship.container.y }
      : this.camera;
  }

  getNavigationContext(): NavigationContext | null {
    const world = this.snapshot;
    const pilot = world?.players.find((ship) => ship.id === this.localPlayerId);
    if (!world || !pilot || !this.flightRunId) return null;
    return {
      world,
      pilot,
      position: this.getLocalPosition(),
      receivedAt: this.snapshotReceivedAt,
      runId: this.flightRunId,
      intro: this.flightIntro,
    };
  }

  toWorld(point: Point): Point {
    return screenToWorld(
      point,
      this.camera,
      this.app.screen.width,
      this.app.screen.height,
      this.zoom,
    );
  }

  pickTarget(point: Point): string {
    let nearest = "";
    let distance = 58;
    for (const [id, ship] of this.ships) {
      if (!this.isAttackable(id)) continue;
      const screen = worldToScreen(
        ship.container,
        this.camera,
        this.app.screen.width,
        this.app.screen.height,
        this.zoom,
      );
      const candidate = Math.hypot(screen.x - point.x, screen.y - point.y);
      if (
        candidate < Math.max(38, ship.state.radius * this.zoom * 1.7) &&
        candidate < distance
      ) {
        distance = candidate;
        nearest = id;
      }
    }
    const pilot = this.snapshot?.players.find((ship) => ship.id === this.localPlayerId);
    if (pilot && this.snapshot)
      for (const rock of attackableTargets(this.snapshot, pilot, this.friends)) {
        if (rock.kind !== "asteroid") continue;
        const screen = worldToScreen(rock, this.camera, this.app.screen.width, this.app.screen.height, this.zoom);
        const candidate = Math.hypot(screen.x - point.x, screen.y - point.y);
        if (candidate < Math.max(38, rock.radius * this.zoom) && candidate < distance) {
          distance = candidate;
          nearest = rock.id;
        }
      }
    return nearest;
  }

  render(now: number, deltaSeconds: number, input: GameInput): void {
    const width = this.app.screen.width;
    const height = this.app.screen.height;
    this.zoom = Math.min(1.15, Math.max(0.55, Math.min(width, height) / 610));
    const presentation = this.snapshot
      ? this.localMovement.frame(
          this.snapshot,
          input.readIntent(),
          now,
          deltaSeconds,
          this.flightIntro,
          this.friends,
        )
      : undefined;
    for (const [id, ship] of this.ships)
      ship.render(now, id === this.localPlayerId ? presentation : undefined, this.zoom);
    const local = this.ships.get(this.localPlayerId);
    if (local) {
      const fraction =
        this.firstPosition ||
        Math.hypot(
          local.container.x - this.camera.x,
          local.container.y - this.camera.y,
        ) > 400
          ? 1
          : 1 - Math.exp(-deltaSeconds * 16);
      this.camera.x += (local.container.x - this.camera.x) * fraction;
      this.camera.y += (local.container.y - this.camera.y) * fraction;
      this.firstPosition = false;
    }
    this.world.scale.set(this.zoom);
    this.world.position.set(
      width / 2 - this.camera.x * this.zoom,
      height / 2 - this.camera.y * this.zoom,
    );
    const tiltBlend = 1 - Math.exp(-deltaSeconds * 6);
    this.smoothedBackdropTilt.x +=
      (this.backdropTilt.x - this.smoothedBackdropTilt.x) * tiltBlend;
    this.smoothedBackdropTilt.y +=
      (this.backdropTilt.y - this.smoothedBackdropTilt.y) * tiltBlend;
    this.backdrop.render(
      width,
      height,
      this.camera.x,
      this.camera.y,
      now,
      this.zoom,
      this.smoothedBackdropTilt,
      this.reducedMotion?.matches ?? false,
      this.snapshot?.boundaryRules ? local?.state.sectorId ?? "" : undefined,
    );
    this.effects.render(
      now,
      {
        x: this.camera.x - width / (2 * this.zoom),
        y: this.camera.y - height / (2 * this.zoom),
        width: width / this.zoom,
        height: height / this.zoom,
      },
      this.reducedMotion?.matches ?? false,
      this.ships,
    );
    this.beacons.render(now);
    this.drawJoystick(input, width, height);
    this.gates.render(now, this.zoom);
    this.drawNavigation(width, height, input.navigationState);
    this.autopilot.render(
      input.navigationState,
      this.getLocalPosition(),
      this.zoom,
    );
    const occupied = this.navigation.screen.visible
      ? this.navigation.screen.getBounds()
      : this.navigation.world.visible
        ? this.navigation.world.getBounds()
        : undefined;
    this.partyMarkerView.render(
      this.partyMarker,
      this.camera,
      width,
      height,
      this.zoom,
      now,
      occupied,
      this.tutorialGuidanceVisible,
    );
  }

  destroy(): void {
    this.backdrop.destroy();
    this.navigation.destroy();
    this.partyMarkerView.destroy();
    this.gates.destroy();
    this.effects.destroy();
    this.relayViews.destroy();
    this.world.destroy({ children: true });
    this.joystick.destroy();
  }

  private syncTerrain(snapshot: WorldSnapshot): void {
    const rocks = snapshot.obstacles.filter((rock) => !rock.resourceMaxHp);
    const regionId = snapshot.boundaryRules ? snapshot.players.find(ship => ship.id === this.localPlayerId)?.sectorId : undefined;
    const key = `${regionId}:${snapshot.width}:${snapshot.height}:${snapshot.stationX}:${snapshot.stationY}:${rocks.map((obstacle) => `${obstacle.id},${obstacle.x},${obstacle.y},${obstacle.radius}`).join(";")}`;
    if (key === this.staticKey) return;
    this.staticKey = key;
    for (const child of this.terrain.removeChildren())
      child.destroy({ children: true });
    this.ground.clear();
    if (!snapshot.boundaryRules) this.ground.rect(0, 0, snapshot.width, snapshot.height)
      .stroke({ color: 0x456477, alpha: 0.35, width: 3 });
    for (const base of STATIONS.filter(base => !snapshot.boundaryRules || base.sectorId === regionId)) {
    this.ground
      .circle(base.x, base.y, 185)
      .stroke({ color: 0x55d8d1, alpha: 0.12, width: 1 });
    this.ground
      .circle(base.x, base.y, 130)
      .fill({ color: 0x1c6470, alpha: 0.065 })
      .stroke({ color: 0x6ee5d7, alpha: 0.22, width: 1 });
    const station = new Sprite({
      texture: this.textures.station,
      anchor: 0.5,
      x: base.x,
      y: base.y,
    });
    station.width = 260;
    station.scale.y = station.scale.x;
    this.terrain.addChild(station);
    const label = new Text({
      text: `СТАНЦИЯ «${base.name.toUpperCase()}»`,
      style: {
        fontFamily: "system-ui, sans-serif",
        fontSize: 14,
        letterSpacing: 3,
        fill: 0x8ba5b2,
      },
    });
    label.anchor.set(0.5);
    label.position.set(base.x, base.y + 165);
    this.terrain.addChild(label);
    }
    for (const [index, obstacle] of rocks.entries()) {
      const asteroid = new Sprite({
        texture:
          this.textures.asteroids[index % 3] ?? this.textures.asteroids[0],
        anchor: 0.5,
        x: obstacle.x,
        y: obstacle.y,
      });
      asteroid.width = obstacle.radius * 2.2;
      asteroid.scale.y = asteroid.scale.x;
      asteroid.rotation = obstacle.x * 0.07 + obstacle.y * 0.11;
      asteroid.tint = 0xa2a6af;
      this.terrain.addChild(asteroid);
    }
  }

  private drawJoystick(input: GameInput, width: number, height: number): void {
    this.joystick.clear();
    if (!input.isTouchDevice && !input.joystick.active) return;
    const state = input.joystick;
    const x = state.active ? state.x : Math.min(100, width * 0.19);
    const y = state.active ? state.y : height - 90;
    this.joystick
      .circle(x, y, 56)
      .fill({ color: 0x071925, alpha: state.active ? 0.32 : 0.15 })
      .stroke({
        color: 0x77b5c4,
        width: 1.5,
        alpha: state.active ? 0.45 : 0.18,
      });
    this.joystick
      .circle(x, y, 19)
      .stroke({ color: 0x77b5c4, width: 1, alpha: 0.15 });
    this.joystick
      .circle(x + state.dx * 43, y + state.dy * 43, 23)
      .fill({ color: 0x8bdfe6, alpha: state.active ? 0.2 : 0.08 })
      .stroke({
        color: 0xb6f5f2,
        width: 1.5,
        alpha: state.active ? 0.75 : 0.3,
      });
  }

  private drawNavigation(width: number, height: number, course: NavigationState): void {
    const snapshot = this.snapshot;
    const local = this.ships.get(this.localPlayerId);
    if (!snapshot || local?.state.docked) {
      this.navigation.render(
        undefined,
        this.camera,
        width,
        height,
        this.zoom,
      );
      return;
    }
    const objective = this.eventObjective ?? this.mission?.objective;
    let destination: NavigationTarget | undefined;
    const selectedTrainingGate = this.mission?.contractId === "intro" && this.mission.outcome === "success" && objective?.kind === "travel" &&
      (course.status === "planning" || course.status === "flying" || course.status === "arrived")
      ? snapshot.gates.find(gate => TRAINING_GATE_FACTIONS[gate.id] &&
        Math.hypot(gate.x - course.destination.x, gate.y - course.destination.y) <= gate.radius)
      : undefined;
    if (selectedTrainingGate) {
      destination = { name: selectedTrainingGate.name, x: selectedTrainingGate.x, y: selectedTrainingGate.y, kind: "objective", entityVisible: true };
    } else if (objective && objective.kind !== "complete") {
      const ship = objective.targetId
        ? this.ships.get(objective.targetId)
        : undefined;
      destination = {
        name: this.eventObjective
          ? objective.name
          : navigationCaption(this.mission?.contractId, objective),
        x: ship?.container.x ?? objective.x,
        y: ship?.container.y ?? objective.y,
        entityVisible: ship !== undefined && !ship.state.dead && !ship.state.docked && ship.container.visible,
        kind:
          objective.kind === "combat"
            ? "hostile"
            : objective.kind === "return"
              ? "station"
              : "objective",
      };
    } else if (!this.mission && local) {
      const base = nearestStation(local.state, local.state.factionId);
      const gate = firstLocalGate(snapshot, local.state, base.sectorId);
      const selected = this.ships.get(this.selectedTarget);
      destination = selected
        ? {
            name: selected.state.name,
            x: selected.container.x,
            y: selected.container.y,
            kind: "hostile",
            entityVisible: !selected.state.dead && !selected.state.docked && selected.container.visible,
          }
        : {
            name: gate?.name ?? `Станция «${base.name}»`,
            x: gate?.x ?? base.x,
            y: gate?.y ?? base.y,
            kind: "station",
          };
    }
    this.navigation.render(
      destination,
      this.camera,
      width,
      height,
      this.zoom,
      this.tutorialGuidanceVisible,
    );
  }
}
